import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MapMaximizeProps {
  /** Render-prop body. The `maximized` flag is forwarded so the consumer
   *  can render different chrome inside vs. outside fullscreen mode
   *  (e.g. always-visible slider when maximized). */
  children: (state: {
    maximized: boolean;
    /** Pre-built maximize button — drop it inside your map container at
     *  the desired position. Calls `toggle()` on click. */
    button: ReactNode;
    toggle: () => void;
  }) => ReactNode;
  /** Optional class for the outer wrapper (only applied in non-maximized
   *  mode — the maximized state always uses position: fixed inset-0). */
  className?: string;
}

/**
 * Wraps a map view in an opt-in fullscreen overlay. When maximized:
 * - The wrapper takes over the viewport with `fixed inset-0 z-[9999]`.
 * - ESC key exits.
 * - Document scroll is locked so the page underneath doesn't scroll.
 *
 * The render-prop pattern lets the consumer drop the maximize button
 * wherever it makes sense inside their MapContainer's overlay layer.
 */
export function MapMaximize({ children, className }: MapMaximizeProps) {
  const [maximized, setMaximized] = useState(false);

  const toggle = useCallback(() => setMaximized((v) => !v), []);

  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMaximized(false);
    };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while maximized.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [maximized]);

  // Tell Leaflet to recompute size after the container resizes — without
  // this, the tiles get clipped when the map is resized by CSS alone.
  useEffect(() => {
    // Wait one frame so the DOM has the new size before invalidating.
    const id = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
    return () => window.clearTimeout(id);
  }, [maximized]);

  const button = (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      className="shadow-md"
      onClick={toggle}
      aria-label={maximized ? 'Exit fullscreen' : 'Maximize map'}
      title={maximized ? 'Exit fullscreen (ESC)' : 'Maximize'}
    >
      {maximized ? (
        <Minimize2 className="h-4 w-4" />
      ) : (
        <Maximize2 className="h-4 w-4" />
      )}
    </Button>
  );

  return (
    <div
      className={cn(
        maximized
          ? 'fixed inset-0 z-[9999] flex flex-col bg-background'
          // `h-full` lets fill-layout consumers (e.g. the public/parcels maps
          // that pass `!h-full` to the map area) resolve their height through
          // this wrapper. Against an auto-height parent it collapses to auto,
          // so variant-height maps (h-[50vh] etc.) are unaffected; against a
          // definite-height (flex) parent it fills it. Without it the wrapper
          // is 0px tall and the map paints blank.
          : 'relative w-full h-full',
        !maximized && className,
      )}
    >
      {children({ maximized, button, toggle })}
    </div>
  );
}

export default MapMaximize;
